/**
 * Helpers for extracting structured validation errors from the Go API.
 *
 * Server wire shape:
 *   422 → { error: "validation_failed", message, fields: { [field]: { code, message } } }
 *   400 → { error: "invalid_json" | "unknown_field_key", message, [field_key]?, ... }
 *
 * The frontend reads `.message` for the human-readable fallback. Once
 * i18n is wired up here, swap to keying off `.code` via the
 * `errors.validation.${code}` namespace.
 */

import { ApiError } from "./api";
import {
  FORM_RESPONSE_LABELS,
  PERSON_FIELD_LABELS,
  SUBENTITY_FIELD_LABELS,
  SUBENTITY_LABELS,
} from "./field-labels";

export interface FieldErrorEntry {
  code: string;
  message: string;
}

export type FieldErrorMap = Record<string, FieldErrorEntry>;

interface ValidationFailedBody {
  error: "validation_failed";
  message?: string;
  fields: FieldErrorMap;
}

function isFieldErrorEntry(v: unknown): v is FieldErrorEntry {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as { message?: unknown }).message === "string"
  );
}

function isValidationFailed(body: unknown): body is ValidationFailedBody {
  if (typeof body !== "object" || body === null) return false;
  if ((body as { error?: unknown }).error !== "validation_failed") return false;
  const fields = (body as { fields?: unknown }).fields;
  if (typeof fields !== "object" || fields === null) return false;
  // Per-entry shape check — a malformed payload (e.g. `fields:{ssn:"bad"}`
  // with a string instead of the {code, message} object) used to silent-
  // skip on the per-input render. We require every value to be a
  // FieldErrorEntry-shaped object before treating the body as a
  // recognised validation_failed envelope.
  for (const v of Object.values(fields)) {
    if (!isFieldErrorEntry(v)) return false;
  }
  return true;
}

/**
 * Returns the per-field error map when the error came from a 422
 * validation_failed response; null otherwise.
 *
 * Usage in a wizard step:
 *   const fieldErrors = getFieldErrors(update.error);
 *   <input ... />
 *   <FieldError name="given_name" errors={fieldErrors} />
 */
export function getFieldErrors(err: unknown): FieldErrorMap | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 422) return null;
  if (!isValidationFailed(err.body)) return null;
  return err.body.fields;
}

/**
 * Returns a human-friendly form-level message for any error: the
 * server's top-level `message`, the ApiError message, or a generic
 * fallback. Use under the form when you also want to show "something
 * went wrong" even though individual fields already light up red.
 *
 * Returns null for 422 with fields — the field-level UI carries the
 * information, no need for a redundant banner.
 */
export function getFormError(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 422 && isValidationFailed(err.body)) {
      // Field-level rendering covers this when fields is non-empty;
      // for an empty-fields 422 we want SOMETHING to render so the
      // user isn't left guessing why "Save" did nothing.
      if (Object.keys(err.body.fields).length > 0) return null;
      const msg = err.body.message;
      if (typeof msg === "string" && msg) return msg;
      return "Validation failed.";
    }
    if (typeof err.body === "object" && err.body !== null) {
      // Prefer `message` (a human-readable line) and fall back to
      // `error` only if it's a sentence-y string (not a slug like
      // "unknown_field_key"). Heuristic: contains a space.
      const body = err.body as { message?: unknown; error?: unknown };
      if (typeof body.message === "string" && body.message) return body.message;
      if (typeof body.error === "string" && body.error.includes(" ")) return body.error;
    }
    return err.message || `Request failed (${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

/**
 * Form-level message that ALWAYS surfaces something on error — unlike
 * getFormError, this does not return null when 422 fields are present.
 * Use on pages whose inputs are not wired through getFieldErrors (e.g.
 * StepBiographic's ethnicity/race Selects/checkboxes) so the user
 * still sees a clue when a validation_failed response comes back.
 *
 * For 422 + fields: returns a summary like "ethnicity: invalid" so the
 * user knows which key the server rejected. Falls back to message or
 * generic "validation failed".
 */
export function getFormErrorAlways(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError && err.status === 422 && isValidationFailed(err.body)) {
    const entries = Object.entries(err.body.fields);
    if (entries.length === 0) {
      return err.body.message || "Validation failed.";
    }
    return entries
      .map(([field, fe]) => `${field}: ${fe.message ?? "invalid"}`)
      .join("; ");
  }
  return getFormError(err);
}

/**
 * Run a batch of upsert promises and return the rejections without
 * throwing. Used by wizard steps that fire many form_response upserts
 * in parallel — Promise.all + a shared react-query mutation hook
 * loses errors in the cross-fire (each successful mutateAsync resets
 * the mutation state, so a failing call's error gets cleared by the
 * next succeeding call). Promise.allSettled lets us inspect every
 * outcome, and the caller stitches them into a combined field-error
 * map.
 */
export async function runUpserts(
  upserts: Promise<unknown>[],
): Promise<unknown[]> {
  const results = await Promise.allSettled(upserts);
  return results
    .filter((r): r is PromiseRejectedResult => r.status === "rejected")
    .map((r) => r.reason);
}

/**
 * Merge field-level errors from multiple ApiErrors into a single map.
 * Later errors overwrite earlier ones for the same field (rare —
 * each upsert touches a distinct field_key). Returns null when no
 * 422-with-fields was present.
 */
export function combineFieldErrors(errors: unknown[]): FieldErrorMap | null {
  const out: FieldErrorMap = {};
  for (const err of errors) {
    const fe = getFieldErrors(err);
    if (fe) Object.assign(out, fe);
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * First form-level (non-422) message across a batch of errors. Used
 * as the banner copy when at least one upsert blew up with a network
 * / 5xx / 423 identity-lock response rather than validation_failed.
 */
export function combineFormError(errors: unknown[]): string | null {
  for (const err of errors) {
    const m = getFormError(err);
    if (m) return m;
  }
  return null;
}

/**
 * Convert an EvaluationIssue.key path emitted by the backend snapshot
 * walker into a human-readable label suitable for the readiness UI.
 * Consults the registries in `field-labels.ts` first so the wording
 * matches the wizard's `<Field label>` exactly; falls back to a
 * snake_case → Title Case transform with a small acronym set for any
 * key the registry hasn't covered yet (so a new backend issue type
 * surfaces as ugly-but-readable rather than blank).
 *
 * Examples:
 *   form_responses.ssn                         → "SSN"
 *   form_responses.interpreter_signature_date  → "Interpreter's signature date"
 *   persons[0].given_name                      → "Given (first) name"
 *   persons[0].addresses[1].state              → "Address 2 · State / province"
 *   persons[0].marriages[0].spouse_full_name   → "Marriage 1 · Spouse's full name"
 */
export function humanizeIssueKey(key: string): string {
  if (key.startsWith("form_responses.")) {
    const k = key.slice("form_responses.".length);
    return FORM_RESPONSE_LABELS[k] ?? humanizeFieldName(k);
  }
  // Strip the persons[N]. prefix — N-400 is single-applicant; the
  // index is noise.
  const stripped = key.replace(/^persons\[\d+\]\./, "");
  // Nested sub-entity: addresses[1].state, employments[2].end_date, …
  const nested = stripped.match(/^(\w+)\[(\d+)\]\.(.+)$/);
  if (nested) {
    const entity =
      SUBENTITY_LABELS[nested[1]] ?? humanizeFieldName(singular(nested[1]));
    const idx = parseInt(nested[2], 10) + 1;
    const field =
      SUBENTITY_FIELD_LABELS[nested[3]] ?? humanizeFieldName(nested[3]);
    return `${entity} ${idx} · ${field}`;
  }
  return PERSON_FIELD_LABELS[stripped] ?? humanizeFieldName(stripped);
}

function humanizeFieldName(snake: string): string {
  // Acronyms / abbreviations that should be upper-cased rather than
  // title-cased ("SSN" not "Ssn"). Only the fallback path uses this
  // — anything important is in field-labels.ts.
  const acronyms = new Set(["ssn", "lpr", "uscis", "ssa", "vawa", "us"]);
  return snake
    .split("_")
    .map((word) => {
      if (acronyms.has(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

function singular(s: string): string {
  if (s === "children") return "child";
  return s.endsWith("s") ? s.slice(0, -1) : s;
}

/**
 * True when the error is the 400 unknown_field_key response. Used by
 * form-response writes during the closed alpha — a frontend bug, not
 * a user mistake, so we want a louder dev-time message.
 */
export function isUnknownFieldKey(err: unknown): err is ApiError {
  return (
    err instanceof ApiError &&
    err.status === 400 &&
    typeof err.body === "object" &&
    err.body !== null &&
    (err.body as { error?: unknown }).error === "unknown_field_key"
  );
}
